/**
 * <g-editor> - Markdown WYSIWYG Editor
 *
 * A minimalistic markdown editor built on Milkdown.
 * Provides WYSIWYG editing with toolbar and markdown output.
 *
 * @example
 * <g-editor placeholder="Start writing..."></g-editor>
 *
 * @example With initial content
 * <g-editor value="# Hello\n\nStart editing..."></g-editor>
 *
 * @example Get markdown content
 * const editor = document.querySelector('g-editor');
 * const markdown = editor.getMarkdown();
 *
 * @attr {string} placeholder - Placeholder text when empty
 * @attr {string} value - Initial markdown content
 * @attr {boolean} readonly - Make editor read-only
 *
 * @fires change - When content changes, detail: { markdown }
 */
import { Editor, rootCtx, defaultValueCtx, editorViewCtx, serializerCtx, parserCtx } from '@milkdown/kit/core';
import { commonmark, toggleStrongCommand, toggleEmphasisCommand, wrapInHeadingCommand, wrapInBulletListCommand, wrapInOrderedListCommand, wrapInBlockquoteCommand } from '@milkdown/kit/preset/commonmark';
import { history, undoCommand, redoCommand } from '@milkdown/kit/plugin/history';
import { callCommand } from '@milkdown/kit/utils';
import { Slice } from '@milkdown/kit/prose/model';

export class GEditor extends HTMLElement {
  static get observedAttributes() {
    return ['readonly', 'placeholder'];
  }

  constructor() {
    super();
    this._editor = null;
    this._value = '';
  }

  connectedCallback() {
    this._value = this.getAttribute('value') || '';
    this.render();
  }

  disconnectedCallback() {
    this.destroy();
  }

  async render() {
    // Container styling via Tailwind
    this.classList.add(
      'block', 'rounded-lg', 'border', 'border-base-300',
      'bg-base-100', 'overflow-hidden'
    );

    // Create toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'g-editor-toolbar flex gap-1 p-2 border-b border-base-300 bg-base-200';
    toolbar.innerHTML = `
      <button type="button" data-command="bold" class="btn btn-xs btn-ghost font-bold" title="Bold (Ctrl+B)">B</button>
      <button type="button" data-command="italic" class="btn btn-xs btn-ghost italic" title="Italic (Ctrl+I)">I</button>
      <div class="divider divider-horizontal mx-0"></div>
      <button type="button" data-command="h1" class="btn btn-xs btn-ghost" title="Heading 1">H1</button>
      <button type="button" data-command="h2" class="btn btn-xs btn-ghost" title="Heading 2">H2</button>
      <button type="button" data-command="h3" class="btn btn-xs btn-ghost" title="Heading 3">H3</button>
      <div class="divider divider-horizontal mx-0"></div>
      <button type="button" data-command="bullet" class="btn btn-xs btn-ghost" title="Bullet List">• List</button>
      <button type="button" data-command="ordered" class="btn btn-xs btn-ghost" title="Numbered List">1. List</button>
      <button type="button" data-command="quote" class="btn btn-xs btn-ghost" title="Blockquote">" Quote</button>
      <div class="divider divider-horizontal mx-0"></div>
      <button type="button" data-command="undo" class="btn btn-xs btn-ghost" title="Undo (Ctrl+Z)">↶</button>
      <button type="button" data-command="redo" class="btn btn-xs btn-ghost" title="Redo (Ctrl+Y)">↷</button>
    `;
    this.appendChild(toolbar);

    // Create editor container
    const editorRoot = document.createElement('div');
    editorRoot.className = 'g-editor-root p-4 min-h-[150px] focus-within:outline-none';
    this.appendChild(editorRoot);

    // Initialize Milkdown editor
    this._editor = await Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, editorRoot);
        ctx.set(defaultValueCtx, this._value);
      })
      .use(commonmark)
      .use(history)
      .create();

    // Set readonly if attribute present
    if (this.hasAttribute('readonly')) {
      const view = this._editor.ctx.get(editorViewCtx);
      view.setProps({ editable: () => false });
      toolbar.style.display = 'none';
    }

    // Wire up toolbar buttons
    toolbar.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-command]');
      if (!btn) return;

      const cmd = btn.dataset.command;
      this._runCommand(cmd);

      // Refocus editor
      const view = this._editor.ctx.get(editorViewCtx);
      view.focus();
    });

    // Emit change events on editor updates
    const view = this._editor.ctx.get(editorViewCtx);
    const self = this;
    const originalDispatch = view.dispatch.bind(view);
    view.dispatch = function(tr) {
      originalDispatch(tr);
      if (tr.docChanged) {
        self.dispatchEvent(new CustomEvent('change', {
          detail: { markdown: self.getMarkdown() },
          bubbles: true
        }));
      }
    };
  }

  _runCommand(cmd) {
    if (!this._editor) return;

    switch (cmd) {
      case 'bold':
        this._editor.action(callCommand(toggleStrongCommand.key));
        break;
      case 'italic':
        this._editor.action(callCommand(toggleEmphasisCommand.key));
        break;
      case 'h1':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 1));
        break;
      case 'h2':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 2));
        break;
      case 'h3':
        this._editor.action(callCommand(wrapInHeadingCommand.key, 3));
        break;
      case 'bullet':
        this._editor.action(callCommand(wrapInBulletListCommand.key));
        break;
      case 'ordered':
        this._editor.action(callCommand(wrapInOrderedListCommand.key));
        break;
      case 'quote':
        this._editor.action(callCommand(wrapInBlockquoteCommand.key));
        break;
      case 'undo':
        this._editor.action(callCommand(undoCommand.key));
        break;
      case 'redo':
        this._editor.action(callCommand(redoCommand.key));
        break;
    }
  }

  destroy() {
    if (this._editor) {
      this._editor.destroy();
      this._editor = null;
    }
  }

  /**
   * Get the current markdown content
   * @returns {string} Markdown string
   */
  getMarkdown() {
    if (!this._editor) return '';
    const view = this._editor.ctx.get(editorViewCtx);
    const serializer = this._editor.ctx.get(serializerCtx);
    return serializer(view.state.doc);
  }

  /**
   * Set the markdown content
   * @param {string} markdown - Markdown string to set
   */
  setMarkdown(markdown) {
    if (!this._editor) {
      this._value = markdown;
      return;
    }

    const view = this._editor.ctx.get(editorViewCtx);
    const parser = this._editor.ctx.get(parserCtx);
    const doc = parser(markdown);
    const tr = view.state.tr.replace(0, view.state.doc.content.size, new Slice(doc.content, 0, 0));
    view.dispatch(tr);
  }

  /**
   * Get/set the value property (alias for markdown)
   */
  get value() {
    return this.getMarkdown();
  }

  set value(markdown) {
    this.setMarkdown(markdown);
  }
}

customElements.define('g-editor', GEditor);
